import React from 'react';
import { Clock, Trello, Ticket } from 'lucide-react';
import type { ComputedEventSchedule } from '@/features/events/types/types';
import { formatDateByTimezone, getTimezoneAbbreviation, formatDuration } from '@/lib/utils';

interface BookingSectionProps {
  schedules: ComputedEventSchedule[];
  onBookTable: (schedule: ComputedEventSchedule) => void;
  onBookTickets: (schedule: ComputedEventSchedule) => void;
  businessTimeZone?: string;
  showHeading?: boolean;
}

const BookingSection: React.FC<BookingSectionProps> = ({ 
  schedules, 
  onBookTable, 
  onBookTickets,
  businessTimeZone,
  showHeading = true
}) => {
  const formatTime = (dateString: string) => {
    if (!businessTimeZone) return 'TBD';
    const timeResult = formatDateByTimezone(dateString, businessTimeZone, { format: 'time', includeTimezone: false });
    const timezoneAbbr = getTimezoneAbbreviation(dateString, businessTimeZone);
    
    if (timeResult === "Invalid date provided.") {
      return "Invalid Time";
    }
    
    return `${timeResult} ${timezoneAbbr}`;
  };

  // Helper function to check if a schedule is in the past
  const isSchedulePast = (schedule: ComputedEventSchedule): boolean => {
    const now = new Date();
    const startTime = new Date(schedule.start_time);
    const durationMs = schedule.duration / 1e6; // Convert nanoseconds to milliseconds
    const endTime = new Date(startTime.getTime() + durationMs);
    return endTime <= now;
  };
  

  if (!schedules || schedules.length === 0) {
    return (
      <div className="text-center py-12">
        <h2 className="text-4xl font-serif font-medium text-white text-center mb-4">
          No Upcoming Events
        </h2>
        <p className="text-gray-400">
          Check back later for upcoming event dates.
        </p>
      </div>
    );
  }

  return (
    <div>
      {showHeading && (
        <h2 className="text-4xl font-serif font-medium text-white text-center mb-10">
          Book Your Date
        </h2>
      )}
      <div className="space-y-4">
        {schedules.map((schedule) => (
          <div 
            key={schedule.id} 
            id={schedule.id}
            className="flex flex-col sm:flex-row items-start sm:items-center p-4 border-b border-gray-800/50 hover:bg-gray-900/50 rounded-lg transition-colors duration-300"
          >
            <div className="flex-shrink-0 text-center w-full sm:w-20 mb-3 sm:mb-0">
              <p className="text-sm font-bold text-blue-400 uppercase">
                {(() => {
                  try {
                    const date = new Date(schedule.start_time);
                    
                    if (isNaN(date.getTime())) {
                      return '?';
                    }
                    
                    // Use Intl.DateTimeFormat to get the month in the correct timezone
                    const monthInTz = businessTimeZone 
                      ? new Intl.DateTimeFormat('en-US', {
                          timeZone: businessTimeZone,
                          month: 'short'
                        }).format(date)
                      : date.toLocaleDateString('en-US', { month: 'short' });
                    
                    return monthInTz.toUpperCase();
                  } catch {
                    const fallbackDate = new Date(schedule.start_time);
                    return isNaN(fallbackDate.getTime()) ? '?' : fallbackDate.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
                  }
                })()}
              </p>
              <p className="text-4xl font-bold text-white tracking-tighter">
                {(() => {
                  try {
                    const date = new Date(schedule.start_time);
                    
                    if (isNaN(date.getTime())) {
                      return '?';
                    }
                    
                    // Use Intl.DateTimeFormat to get the day in the correct timezone
                    const dayInTz = businessTimeZone
                      ? new Intl.DateTimeFormat('en-US', {
                          timeZone: businessTimeZone,
                          day: 'numeric'
                        }).format(date)
                      : date.getDate().toString();
                    
                    return dayInTz;
                  } catch {
                    const fallbackDate = new Date(schedule.start_time);
                    return isNaN(fallbackDate.getTime()) ? '?' : fallbackDate.getDate();
                  }
                })()}
              </p>
            </div>
            
            <div className="flex-1 sm:ml-6 mb-3 sm:mb-0">
              <p className="font-semibold text-lg text-white">
                {businessTimeZone ? formatDateByTimezone(schedule.start_time, businessTimeZone, { format: 'weekday', includeTimezone: false }) : 'TBD'}
              </p>
              <p className="text-gray-400">
                <Clock className="inline w-4 h-4 mr-1.5" />
                {formatTime(schedule.start_time)} • {formatDuration(schedule.duration)}
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              {(() => {
                const isPast = isSchedulePast(schedule);
                return (
                  <>
                    <button 
                      onClick={() => !isPast && onBookTable(schedule)} 
                      disabled={isPast}
                      className={`group flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold rounded-full transition-all ${
                        isPast 
                          ? 'text-gray-500 border border-gray-800 bg-gray-900/30 cursor-not-allowed opacity-60' 
                          : 'text-gray-300 border border-gray-700 hover:bg-gray-800 hover:text-white cursor-pointer'
                      }`}
                    >
                      <Trello className={`w-4 h-4 transition-colors ${
                        isPast ? 'text-gray-600' : 'text-gray-500 group-hover:text-white'
                      }`} />
                      Book a Table
                    </button>
                    <button 
                      onClick={() => !isPast && onBookTickets(schedule)} 
                      disabled={isPast}
                      className={`group flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold rounded-full transition-all ${
                        isPast 
                          ? 'text-gray-500 bg-gray-900/30 border border-gray-800 cursor-not-allowed opacity-60' 
                          : 'text-white bg-blue-600 hover:bg-blue-500 cursor-pointer'
                      }`}
                    >
                      <Ticket className="w-4 h-4" />
                      Book Tickets
                    </button>
                  </>
                );
              })()}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BookingSection;
